import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { format, parseISO } from 'date-fns'
import { it as itLocale, enUS } from 'date-fns/locale'
import { ChevronRight } from 'lucide-react'
import { Button } from '../ui/button'
import { cn } from '../../lib/utils'
import type { WorkoutResult } from '../../types/workout'

const EFFORT_DOT_CLASSES = [
  'bg-yellow-400',
  'bg-blue-500',
  'bg-green-500',
  'bg-orange-400',
  'bg-red-500',
]

function EffortDots({ effort }: { effort: number }) {
  return (
    <div className="flex gap-0.5 items-center shrink-0">
      {Array.from({ length: 5 }, (_, i) => (
        <span
          key={i}
          className={cn(
            'size-2 rounded-full',
            i < effort ? EFFORT_DOT_CLASSES[effort - 1] : 'bg-muted-foreground/25'
          )}
        />
      ))}
    </div>
  )
}

interface ResultRowProps {
  result: WorkoutResult
  /** Hide the trailing "view" button (e.g. when displayed on the result's own page). */
  hideViewButton?: boolean
  /** When the row is rendered from a workout context, propagate it so the result page
   *  can show a clickable "Exercise" breadcrumb segment that links back to this workout. */
  fromWorkoutId?: string
}

export function ResultRow({ result, hideViewButton = false, fromWorkoutId }: ResultRowProps) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [expanded, setExpanded] = useState(false)

  const dateFnsLocale = i18n.language?.startsWith('it') ? itLocale : enUS
  const dateLabel = format(parseISO(result.completed_on), 'EEE d MMM yyyy', { locale: dateFnsLocale })

  return (
    <div className="rounded-md border bg-card p-3 flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">{dateLabel}</span>
        {!hideViewButton && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 text-xs px-2"
            onClick={() => navigate(
              `/training-diary/workout-results/${result.id}`,
              fromWorkoutId ? { state: { fromWorkoutId } } : undefined
            )}
          >
            {t('trainingDiary.workouts.results.viewButton', 'View')}
            <ChevronRight className="size-3 ml-1" />
          </Button>
        )}
      </div>

      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
            {t('trainingDiary.workouts.result.effortLabel', 'Effort')}
          </span>
          <EffortDots effort={result.effort} />
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
            {t('trainingDiary.workouts.result.completionLabel', 'Completion')}
          </span>
          <span className="text-sm font-medium">{result.completion_percent}%</span>
        </div>
      </div>

      {result.note && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className={cn(
            'text-xs text-foreground/80 text-left w-full whitespace-pre-wrap',
            !expanded && 'line-clamp-2'
          )}
        >
          {result.note}
        </button>
      )}
    </div>
  )
}
